import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Header from "../components/Header";
import Nav from "../components/Nav";
import ApplicationToggle from "../components/ApplicationToggle";
import { getApplicationTags, hasApplicationTag } from "../utils/applicationTags";

const PAGE_SIZE = 100;

type Source = "silver" | "gold";

interface TaggedAsset {
  data_asset_name?: string;
  job_name?: string;
  schema?: string;
  market?: string;
  gdo?: string;
  gdo_num?: string;
  application?: string;
  is_active?: string | boolean;
  days?: any[];
  [extra: string]: any;
}

interface ModalState {
  asset: TaggedAsset | null;
  name: string;
  schema: string;
  market: string;
  gdo: string;
  gdo_type: string;
  application: string;
  monitored: boolean;
  expected_start: string;
  expected_end: string;
  fs61_start: string;
  fs61_end: string;
  our_sla_start: string;
  our_sla_end: string;
  comment: string;
}

function isMonitored(a: TaggedAsset): boolean {
  const v = String(a.is_active).toLowerCase();
  return v !== "no" && v !== "false";
}

const AssetTagging: React.FC = () => {
  const [source, setSource] = useState<Source>("silver"); // 'silver' | 'gold'
  const [assets, setAssets] = useState<TaggedAsset[]>([]);
  const [sync, setSync] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [market, setMarket] = useState("");
  const [application, setApplication] = useState("");
  const [monitoredFilter, setMonitoredFilter] = useState("");
  const [page, setPage] = useState(1);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [modal, setModal] = useState<ModalState | null>(null); // null | { asset: <existing or null> }
  const [modalErr, setModalErr] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const handleLoad = useCallback(async (q: string) => {
    setLoading(true);
    setError("");
    try {
      const { ok, data, status } = await api.assetTaggingList(source, q.trim());
      if (!ok) {
        throw new Error((data && data.error) || `HTTP ${status}`);
      }
      setAssets((data && data.assets) || []);
      setSync(data && data.sync);
    } catch (e: any) {
      setError("Error loading data: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    setSearch(""); setMarket(""); setApplication(""); setMonitoredFilter(""); setPage(1); setOpenGroups(new Set());
    handleLoad("");
  }, [source, handleLoad]);

  useEffect(() => {
    const id = setTimeout(() => handleLoad(search), 400);
    return () => clearTimeout(id);
  }, [search, handleLoad]);

  const markets = useMemo(() => Array.from(new Set(assets.map((a) => a.market).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)), [assets]);

  const filteredAssets = useMemo(() => {
    let filtered = market ? assets.filter((a) => a.market === market) : assets;
    if (source !== "gold" && application) filtered = filtered.filter((a) => hasApplicationTag(a.application, application));
    if (monitoredFilter) filtered = filtered.filter((a) => isMonitored(a) === (monitoredFilter === "yes"));
    return filtered;
  }, [assets, market, application, monitoredFilter, source]);

  useEffect(() => setPage(1), [market, application, monitoredFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredAssets.length / PAGE_SIZE));
  const pageAssets = filteredAssets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleGroup(key: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleToggleActive(a: TaggedAsset, checked: boolean) {
    const isActive = checked ? "Yes" : "No";
    const isGold = source === "gold";
    const payload = isGold
      ? { schema: a.schema || "", job_name: a.job_name, is_active: isActive }
      : { data_asset_name: a.data_asset_name, is_active: isActive };
    setAssets((prev) => prev.map((x) => (x === a ? { ...x, is_active: isActive } : x)));
    await api.assetTaggingToggle(source, payload);
  }

  async function handleApplicationChange(a: TaggedAsset, value: string) {
    setAssets((prev) => prev.map((x) => (x === a ? { ...x, application: value } : x)));
    await api.assetTaggingApplication({ data_asset_name: a.data_asset_name, application: value });
  }

  function openAddModal() {
    setModalErr("");
    setModal({ asset: null, name: "", schema: "", market: "", gdo: "", gdo_type: "", application: "", monitored: true, expected_start: "", expected_end: "", fs61_start: "", fs61_end: "", our_sla_start: "", our_sla_end: "", comment: "" });
  }
  function openEditModal(a: TaggedAsset) {
    setModalErr("");
    setModal({
      asset: a,
      name: (source === "gold" ? a.job_name : a.data_asset_name) || "",
      schema: a.schema || "",
      market: a.market || "",
      gdo: a.gdo_num || a.gdo || "",
      gdo_type: a.job_type || "",
      application: a.application || "",
      monitored: isMonitored(a),
      expected_start: a.expected_start || "",
      expected_end: a.expected_end || "",
      fs61_start: a.fs61_start || "",
      fs61_end: a.fs61_end || "",
      our_sla_start: a.our_sla_start || "",
      our_sla_end: a.our_sla_end || "",
      comment: a.comment || "",
    });
  }
  function closeModal() { setModal(null); }

  async function saveModal() {
    if (!modal) return;
    const isGold = source === "gold";
    const name = modal.name.trim();
    const schema = modal.schema.trim();
    if (!name) { setModalErr("Name is required."); return; }
    if (isGold && !schema) { setModalErr("Schema is required."); return; }

    const body = isGold
      ? {
          schema,
          job_name: name,
          market: modal.market.trim(),
          gdo_num: modal.gdo.trim(),
          gdo_type: modal.gdo_type.trim(),
          is_active: modal.monitored ? "Yes" : "No",
          expected_start: modal.expected_start.trim(),
          expected_end: modal.expected_end.trim(),
          fs61_start: modal.fs61_start.trim(),
          fs61_end: modal.fs61_end.trim(),
          our_sla_start: modal.our_sla_start.trim(),
          our_sla_end: modal.our_sla_end.trim(),
          comment: modal.comment.trim(),
        }
      : { data_asset_name: name, market: modal.market.trim(), gdo: modal.gdo.trim(), application: modal.application.trim(), is_active: modal.monitored ? "Yes" : "No" };

    setModalSaving(true);
    try {
      const { ok, data } = await api.assetTaggingUpsert(source, body);
      if (!ok) throw new Error((data && data.error) || "Save failed");
      closeModal();
      await handleLoad("");
    } catch (e: any) {
      setModalErr("Save failed: " + e.message);
    } finally {
      setModalSaving(false);
    }
  }

  return (
    <>
      <Header title="Data Assets Intelligence" />
      <Nav />

      <div className="subtabs">
        <div className={`subtab ${source === "silver" ? "active" : ""}`} onClick={() => setSource("silver")}>Silver</div>
        <div className={`subtab ${source === "gold" ? "active" : ""}`} onClick={() => setSource("gold")}>Gold</div>
      </div>

      <div className="controls-bar">
        <input type="text" placeholder="Search data asset name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={market} onChange={(e) => setMarket(e.target.value)}>
          <option value="">All Markets</option>
          {markets.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {source !== "gold" && <ApplicationToggle value={application} onChange={setApplication} id="asset-app-toggle" />}
        <select value={monitoredFilter} onChange={(e) => setMonitoredFilter(e.target.value)}>
          <option value="">All Assets</option>
          <option value="yes">Monitored only</option>
          <option value="no">Not monitored</option>
        </select>
        <div className="spacer" />
        <span className="sync-info">
          {sync ? (sync.last_synced_at ? `Azure SQL sync: ${String(sync.last_synced_at).replace("T", " ").replace("Z", " UTC")}${sync.total != null ? ` \u00b7 ${sync.monitored ?? 0}/${sync.total} monitored` : ""}` : "Not yet synced with Azure SQL") : ""}
        </span>
        <span className="asset-count">{filteredAssets.length} data asset{filteredAssets.length === 1 ? "" : "s"}</span>
        <button className="btn-sm btn-primary" onClick={openAddModal}>&#43; Add Asset</button>
      </div>

      <div className="asset-list-wrap">
        {loading && <div className="loading"><span className="spinner"></span> Loading data assets&hellip;</div>}
        {error && <div className="loading">{error}</div>}
        {!loading && !error && filteredAssets.length === 0 && <div className="no-data-msg">No data assets found.</div>}
        {!loading && !error && pageAssets.map((a, localI) => {
          const i = (page - 1) * PAGE_SIZE + localI;
          const isGold = source === "gold";
          const name = isGold ? a.job_name : a.data_asset_name;
          const key = isGold ? `${a.schema}||${a.job_name}` : (a.data_asset_name as string);
          const active = isMonitored(a);
          const open = openGroups.has(key);
          const appTags = isGold ? [] : getApplicationTags(a.application);
          return (
            <div className={`asset-group ${open ? "open" : ""}`} key={key}>
              <div className="asset-header" onClick={() => toggleGroup(key)}>
                <span className="expand-arrow">&#9654;</span>
                <span className="asset-name" title={name}>{name}</span>
                {a.market && <span className="asset-chip">{a.market}</span>}
                {(a.gdo_num || a.gdo) && <span className="asset-chip">GDO {a.gdo_num || a.gdo}</span>}
                {isGold && a.schema && <span className="asset-chip">{a.schema}</span>}
                {appTags.map((tag) => <span key={tag} className={`app-chip ${tag.toLowerCase()}`}>{tag}</span>)}
                <button className="btn-edit" onClick={(e) => { e.stopPropagation(); openEditModal(a); }}>&#9998; Edit</button>
                <label className={`active-toggle ${active ? "" : "is-inactive"}`} onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={active} onChange={(e) => handleToggleActive(a, e.target.checked)} /> Monitored
                </label>
              </div>
              <div className="asset-detail">
                <div className="detail-row"><span className="dk">Market</span><span className="dv">{a.market || "\u2014"}</span></div>
                <div className="detail-row"><span className="dk">GDO</span><span className="dv">{a.gdo_num || a.gdo || "\u2014"}</span></div>
                {!isGold && (
                  <div className="detail-row">
                    <span className="dk">Application</span>
                    <span className="dv">
                      <input
                        className="app-input" defaultValue={a.application || ""} placeholder="e.g. IBP; Mosaic"
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => { if (e.target.value !== (a.application || "")) handleApplicationChange(a, e.target.value); }}
                      />
                    </span>
                  </div>
                )}
                {(a.days || []).length ? (
                  <table className="run-days-table">
                    <thead><tr><th>Run Days</th><th>Hist Start</th><th>Hist End</th></tr></thead>
                    <tbody>
                      {(a.days || []).map((d: any, di: number) => (
                        <tr key={di}><td>{d.day_of_week}</td><td>{d.hist_avg_start || "\u2014"}</td><td>{d.hist_avg_end || "\u2014"}</td></tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-days-msg">No run-day history computed yet for this asset.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="pagination" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", fontSize: ".8rem" }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>&#8592; Prev</button>
        <span className="pg-info">Page {page} of {totalPages} ({filteredAssets.length} total)</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next &#8594;</button>
      </div>

      {modal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{modal.asset ? "Edit" : "Add"} {source === "gold" ? "Gold Job" : "Silver Data Asset"}</h3>
            <div className="modal-body">
              {source === "gold" && (
                <label>Schema
                  <input type="text" placeholder="e.g. europe" value={modal.schema} readOnly={!!modal.asset}
                    onChange={(e) => setModal({ ...modal, schema: e.target.value })} />
                </label>
              )}
              <label>Name
                <input type="text" placeholder="Data asset / job name" value={modal.name} readOnly={!!modal.asset}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })} />
              </label>
              <label>Market
                <input type="text" placeholder="e.g. FR-BNLX" value={modal.market}
                  onChange={(e) => setModal({ ...modal, market: e.target.value })} />
              </label>
              <label>GDO
                <input type="text" placeholder="e.g. GDO20" value={modal.gdo}
                  onChange={(e) => setModal({ ...modal, gdo: e.target.value })} />
              </label>
              {source === "gold" && (
                <label>GDO Type
                  <input type="text" placeholder="e.g. Sales & Operations" value={modal.gdo_type}
                    onChange={(e) => setModal({ ...modal, gdo_type: e.target.value })} />
                </label>
              )}
              {source !== "gold" && (
                <label>Application
                  <input type="text" placeholder="e.g. IBP; Mosaic" value={modal.application}
                    onChange={(e) => setModal({ ...modal, application: e.target.value })} />
                </label>
              )}
              {source === "gold" && (
                <>
                  <p style={{ fontSize: ".72rem", color: "#64748b", margin: "2px 0 6px", fontWeight: 600 }}>Expected Schedule (all days)</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <label>Exp Start
                      <input type="text" placeholder="HH:MM (overrides hist avg)" value={modal.expected_start}
                        onChange={(e) => setModal({ ...modal, expected_start: e.target.value })} />
                    </label>
                    <label>Exp End
                      <input type="text" placeholder="HH:MM" value={modal.expected_end}
                        onChange={(e) => setModal({ ...modal, expected_end: e.target.value })} />
                    </label>
                  </div>
                  <p style={{ fontSize: ".72rem", color: "#64748b", margin: "8px 0 6px", fontWeight: 600 }}>Reference Metadata</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <label>FS61 Start
                      <input type="text" placeholder="HH:MM" value={modal.fs61_start}
                        onChange={(e) => setModal({ ...modal, fs61_start: e.target.value })} />
                    </label>
                    <label>FS61 End
                      <input type="text" placeholder="HH:MM" value={modal.fs61_end}
                        onChange={(e) => setModal({ ...modal, fs61_end: e.target.value })} />
                    </label>
                    <label>Our SLA Start
                      <input type="text" placeholder="HH:MM" value={modal.our_sla_start}
                        onChange={(e) => setModal({ ...modal, our_sla_start: e.target.value })} />
                    </label>
                    <label>Our SLA End
                      <input type="text" placeholder="HH:MM" value={modal.our_sla_end}
                        onChange={(e) => setModal({ ...modal, our_sla_end: e.target.value })} />
                    </label>
                  </div>
                  <label>Comment
                    <input type="text" placeholder="Optional note" value={modal.comment}
                      onChange={(e) => setModal({ ...modal, comment: e.target.value })} />
                  </label>
                  {modal.asset && (
                    <p style={{ fontSize: ".72rem", color: "#888", margin: "4px 0 0" }}>
                      Changes apply to all days. Fine-tune per day in Gold Reference. Leave Exp Start/End blank to keep auto-computed values.
                    </p>
                  )}
                </>
              )}
              <label className="modal-check">
                <input type="checkbox" checked={modal.monitored} onChange={(e) => setModal({ ...modal, monitored: e.target.checked })} /> Monitored
              </label>
              <div className="modal-err">{modalErr}</div>
            </div>
            <div className="modal-actions">
              <button className="btn-sm btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="btn-sm btn-primary" disabled={modalSaving} onClick={saveModal}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetTagging;
